/**
 * WebSocket client for real-time features (raffle)
 */

import { config } from "@/config";

// =============================================================================
// Types - mirrored from API
// =============================================================================

export type RaffleStatus = "DRAFT" | "READY" | "IN_PROGRESS" | "COMPLETED";

// Client -> Server Events
export interface ClientJoinRaffleEvent {
  type: "join_raffle";
  raffleId: string;
  accountId?: string;
}

export interface ClientLeaveRaffleEvent {
  type: "leave_raffle";
  raffleId: string;
}

export interface ClientPingEvent {
  type: "ping";
}

export type ReactionEmoji = "🔥" | "❤️" | "🎉";

export interface ClientReactionEvent {
  type: "reaction";
  raffleId: string;
  emoji: ReactionEmoji;
}

export type ClientEvent =
  | ClientJoinRaffleEvent
  | ClientLeaveRaffleEvent
  | ClientPingEvent
  | ClientReactionEvent;

// Server -> Client Events
export interface ServerPongEvent {
  type: "pong";
  timestamp: number;
}

export interface ServerErrorEvent {
  type: "error";
  code: string;
  message: string;
}

export interface ServerJoinedEvent {
  type: "joined";
  raffleId: string;
}

export interface ServerLeftEvent {
  type: "left";
  raffleId: string;
}

export interface RaffleState {
  id: string;
  name: string;
  description: string | null;
  status: RaffleStatus;
  isTest: boolean;
  eventId: string;
  eventTitle: string;
}

export interface PrizeInfo {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isDrawn?: boolean;
}

export interface DrawInfo {
  prizeId: string;
  prizeName: string;
  prizeDescription: string | null;
  ticketNumber: string;
  winnerName: string;
  winnerUsername: string | null;
}

export interface ServerRaffleStateEvent {
  type: "raffle:state";
  raffle: RaffleState;
  currentPrize: PrizeInfo | null;
  prizes: Array<PrizeInfo & { isDrawn: boolean }>;
  ticketCount: number;
  ticketNumbers: string[]; // All ticket numbers for water meter effect
  completedDraws: DrawInfo[];
  /** ID of active (READY/IN_PROGRESS) raffle for same event, for redirect */
  activeRaffleId?: string | null;
}

export interface ServerDrawStartEvent {
  type: "raffle:draw_start";
  raffleId: string;
  prizeId: string;
  prize: PrizeInfo;
  durationMs: number;
  ticketNumbers: string[];
}

export interface ServerDrawProgressEvent {
  type: "raffle:draw_progress";
  raffleId: string;
  prizeId: string;
  currentTicketNumber: string;
  progress: number;
}

export interface WinnerInfo {
  ticketNumber: string;
  accountId: string;
  name: string;
  username: string | null;
}

export interface ServerDrawResultEvent {
  type: "raffle:draw_result";
  raffleId: string;
  prizeId: string;
  prize: PrizeInfo;
  winner: WinnerInfo;
}

export interface ServerWinnerEvent {
  type: "raffle:winner";
  raffleId: string;
  prizeId: string;
  prize: PrizeInfo;
  ticketNumber: string;
}

export interface ServerRaffleCompletedEvent {
  type: "raffle:completed";
  raffleId: string;
  totalPrizes: number;
  draws: DrawInfo[];
}

export interface ReactionBurstItem {
  emoji: ReactionEmoji;
  count: number;
}

export interface ServerReactionBurstEvent {
  type: "raffle:reaction_burst";
  raffleId: string;
  reactions: ReactionBurstItem[];
}

export interface ServerNextPrizeEvent {
  type: "raffle:next_prize";
  raffleId: string;
  currentPrize: PrizeInfo | null;
  ticketNumbers: string[]; // Updated list without won tickets
}

// Event to show winner on broadcast screen (admin-controlled)
export interface ServerShowWinnerEvent {
  type: "raffle:show_winner";
  raffleId: string;
  prizeId: string;
  prize: PrizeInfo;
  winner: WinnerInfo;
}

// Event to show completed/final screen on broadcast (admin-controlled)
export interface ServerShowCompletedEvent {
  type: "raffle:show_completed";
  raffleId: string;
  draws: DrawInfo[];
}

// Test raffle reset event - clears all draws and resets to READY state
export interface ServerTestResetEvent {
  type: "raffle:test_reset";
  raffleId: string;
}

export type ServerEvent =
  | ServerPongEvent
  | ServerErrorEvent
  | ServerJoinedEvent
  | ServerLeftEvent
  | ServerRaffleStateEvent
  | ServerDrawStartEvent
  | ServerDrawProgressEvent
  | ServerDrawResultEvent
  | ServerWinnerEvent
  | ServerRaffleCompletedEvent
  | ServerReactionBurstEvent
  | ServerNextPrizeEvent
  | ServerShowWinnerEvent
  | ServerShowCompletedEvent
  | ServerTestResetEvent;

// =============================================================================
// WebSocket Client
// =============================================================================

export interface WebSocketClientOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onMessage?: (event: ServerEvent) => void;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

export class RaffleWebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private options: WebSocketClientOptions;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isClosedIntentionally = false;
  private currentRaffleId: string | null = null;
  private accountId: string | null = null;

  constructor(options: WebSocketClientOptions = {}) {
    // Convert HTTP URL to WebSocket URL
    const apiUrl = config.apiUrl;
    const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    const baseUrl = apiUrl.replace(/^https?/, wsProtocol);
    this.url = `${baseUrl}/ws/raffle`;
    
    this.options = {
      reconnectDelay: 500, // Start with 500ms
      maxReconnectAttempts: 15,
      ...options,
    };
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isClosedIntentionally = false;

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log("[WS] Connected");
        this.reconnectAttempts = 0;
        this.options.onConnect?.();

        // Rejoin room if we were in one
        if (this.currentRaffleId) {
          this.joinRaffle(this.currentRaffleId, this.accountId ?? undefined);
        }
      };

      this.ws.onclose = () => {
        console.log("[WS] Disconnected");
        this.options.onDisconnect?.();

        if (!this.isClosedIntentionally) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error("[WS] Error:", error);
        this.options.onError?.(new Error("WebSocket error"));
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as ServerEvent;
          this.options.onMessage?.(data);
        } catch (error) {
          console.error("[WS] Failed to parse message:", error);
        }
      };
    } catch (error) {
      console.error("[WS] Failed to connect:", error);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.isClosedIntentionally = true;
    this.currentRaffleId = null;
    this.accountId = null;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= (this.options.maxReconnectAttempts ?? 15)) {
      console.log("[WS] Max reconnect attempts reached");
      return;
    }

    this.reconnectAttempts++;
    // Faster reconnect: 500ms, 750ms, 1125ms, 1687ms, 2531ms, max 5000ms
    const baseDelay = this.options.reconnectDelay ?? 500;
    const delay = Math.min(baseDelay * Math.pow(1.5, this.reconnectAttempts - 1), 5000);
    
    console.log(`[WS] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private send(event: ClientEvent): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(event));
    }
  }

  joinRaffle(raffleId: string, accountId?: string): void {
    this.currentRaffleId = raffleId;
    this.accountId = accountId ?? null;
    
    this.send({
      type: "join_raffle",
      raffleId,
      accountId,
    });
  }

  leaveRaffle(raffleId: string): void {
    if (this.currentRaffleId === raffleId) {
      this.currentRaffleId = null;
      this.accountId = null;
    }
    
    this.send({
      type: "leave_raffle",
      raffleId,
    });
  }

  ping(): void {
    this.send({ type: "ping" });
  }

  sendReaction(raffleId: string, emoji: ReactionEmoji): void {
    this.send({
      type: "reaction",
      raffleId,
      emoji,
    });
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
let wsClient: RaffleWebSocketClient | null = null;

export function getRaffleWebSocket(): RaffleWebSocketClient {
  if (!wsClient) {
    wsClient = new RaffleWebSocketClient();
  }
  return wsClient;
}
